import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListView } from '../../design-system/list-view/ListView'
import { RowActionsMenu } from '../../design-system/list-view/RowActionsMenu'
import type { ActiveFilterChip } from '../../design-system/list-view/FilterPanel'
import type { Density, ExportFormat, SortSpec } from '../../design-system/list-view/types'
import { usePersistentState } from '../../lib/usePersistentState'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { useSavedViews } from '../../lib/savedViews'
import { rsqlAnd, rsqlEquals, rsqlSearch } from '../../lib/rsql'
import { runExport, exportCapNote, type ExportColumn } from '../../lib/export'
import { listBranches, patchBranch } from '../../api/branches'
import { ApiError } from '../../api/problem'
import type { BranchView } from '../../types/branch'
import { LegalEntityLookupField } from './LegalEntityLookupField'
import { BRANCH_COLUMN_ORDER, buildBranchColumns } from './columns'
import { BranchFormModal } from './BranchFormModal'

const PAGE_LIMIT = 50

interface FacetState {
  legalEntity: { id: string; name: string } | null
  permanentEstablishmentOnly: boolean
}

function emptyFacets(): FacetState {
  return { legalEntity: null, permanentEstablishmentOnly: false }
}

function buildFilter(facets: FacetState, search: string): string | null {
  const legalEntityClause = facets.legalEntity ? rsqlEquals('legalEntityId', facets.legalEntity.id) : null
  // Not `rsqlEquals` — that helper double-quotes its value for string
  // comparisons, which would send `isPermanentEstablishment=="true"` (a
  // string literal) instead of the boolean literal the backend's RSQL
  // boolean column comparison expects.
  const peClause = facets.permanentEstablishmentOnly ? 'isPermanentEstablishment==true' : null
  const searchClause = rsqlSearch(['name', 'country', 'region'], search)
  return rsqlAnd(legalEntityClause, peClause, searchClause) || null
}

function buildSort(sort: SortSpec[]): string | null {
  if (sort.length === 0) return null
  return sort.map((s) => (s.direction === 'desc' ? `-${s.columnId}` : s.columnId)).join(',')
}

/** Everything a saved view needs to capture for this list (Saved Views/Filters story). `FacetState`
 * is already JSON-serializable, so it is captured verbatim. */
interface BranchesSavedViewState {
  facets: FacetState
  search: string
  sort: SortSpec[]
  columnOrder: string[]
  hiddenColumnIds: string[]
  pinnedColumnIds: string[]
  density: Density
}

/**
 * Branches (IB-48 AC15) — net-new nav surface and route
 * (`Clients → Branches → /clients/branches`, ratified UX placement). Columns
 * carry the Legal entity parent per CLAUDE.md §4's cross-list rule (the
 * master-data subset: Branch shows its Legal entity, no Case/Fiscal year —
 * those only apply to case-scoped lists). Row-click now navigates to the
 * real `BranchDetailScreen` (`/clients/branches/:id`, CRM completeness —
 * IB-93), mirroring `AccountsListScreen`; `BranchFormModal` remains
 * available as a distinct "Edit" row action for the fast in-place path.
 */
export function BranchesListScreen() {
  const navigate = useNavigate()

  const [branches, setBranches] = useState<BranchView[]>([])

  /**
   * IB-27 S3 — the `region` retrofit target (design §4c). `commitBranchRegion` is the ONLY write
   * path this column ever calls: the existing capability-gated `PATCH /branches/{id}`, never
   * `AdjustmentService` (`region` is not a guarded field; `isPermanentEstablishment` deliberately
   * stays off the grid — see `columns.tsx`). Rejection propagates as-is — `ApiError` is already an
   * `Error` whose `.message` is the server's exact `problem.detail` (AC5).
   */
  const commitBranchRegion = useCallback(async (row: BranchView, next: string) => {
    const trimmed = next.trim()
    const { branch } = await patchBranch(row.id, { region: trimmed }, row.version)
    setBranches((prev) => prev.map((b) => (b.id === branch.id ? branch : b)))
  }, [])

  const columns = useMemo(() => buildBranchColumns({ onEditRegion: commitBranchRegion }), [commitBranchRegion])

  const [columnOrder, setColumnOrder] = usePersistentState<string[]>('iw.branches.columnOrder', BRANCH_COLUMN_ORDER)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>(
    'iw.branches.hiddenColumns',
    columns.filter((c) => c.defaultHidden).map((c) => c.id),
  )
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>('iw.branches.pinnedColumns', ['name'])
  const [columnWidths, setColumnWidths] = usePersistentState<Record<string, number>>('iw.branches.columnWidths', {})
  const [density, setDensity] = usePersistentState<Density>('iw.branches.density', 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  const [facets, setFacets] = useState<FacetState>(emptyFacets)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const [sort, setSort] = useState<SortSpec[]>([{ columnId: 'name', direction: 'asc' }])
  const [selection, setSelection] = useState<Set<string>>(new Set())

  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; branch: BranchView } | null>(null)

  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filterString = useMemo(() => buildFilter(facets, debouncedSearch), [facets, debouncedSearch])
  const sortString = useMemo(() => buildSort(sort), [sort])
  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await listBranches({ filter: filterString, sort: sortString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setBranches((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load branches. Check your connection and try again.')
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [filterString, sortString],
  )

  useEffect(() => {
    setSelection(new Set())
    void fetchPage(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString])

  function retry() {
    void fetchPage(null, true)
  }

  const quickFilters = useMemo(
    () => [
      { id: 'all', label: 'All' },
      { id: 'permanent-establishment', label: 'Permanent establishment' },
    ],
    [],
  )
  const activeQuickFilterIds = useMemo(() => {
    const ids = new Set<string>()
    if (!facets.permanentEstablishmentOnly) ids.add('all')
    if (facets.permanentEstablishmentOnly) ids.add('permanent-establishment')
    return ids
  }, [facets])

  function onQuickFilterToggle(id: string) {
    if (id === 'all') {
      setFacets((prev) => ({ ...prev, permanentEstablishmentOnly: false }))
      return
    }
    if (id === 'permanent-establishment') {
      setFacets((prev) => ({ ...prev, permanentEstablishmentOnly: !prev.permanentEstablishmentOnly }))
    }
  }

  const activeFilterChips: ActiveFilterChip[] = useMemo(() => {
    const chips: ActiveFilterChip[] = []
    if (facets.legalEntity) chips.push({ id: 'legalEntity', label: `Legal entity: ${facets.legalEntity.name}` })
    return chips
  }, [facets])

  function removeFilterChip(id: string) {
    if (id === 'legalEntity') setFacets((prev) => ({ ...prev, legalEntity: null }))
  }

  function clearAllFilters() {
    setFacets(emptyFacets())
    setSearch('')
  }

  const hasAnyFilterOrSearch = Boolean(facets.legalEntity) || facets.permanentEstablishmentOnly || debouncedSearch.trim() !== ''

  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  /** IB-27 S3 — persisted per-user resize widths (`iw.branches.columnWidths`, design §4b). */
  function handleColumnWidthChange(id: string, width: number) {
    setColumnWidths((prev) => ({ ...prev, [id]: width }))
  }

  const savedViewState: BranchesSavedViewState = useMemo(
    () => ({ facets, search, sort, columnOrder, hiddenColumnIds: hiddenColumnIdsArr, pinnedColumnIds: pinnedColumnIdsArr, density }),
    [facets, search, sort, columnOrder, hiddenColumnIdsArr, pinnedColumnIdsArr, density],
  )
  const applySavedViewState = useCallback((state: BranchesSavedViewState) => {
    setFacets(state.facets)
    setSearch(state.search)
    setSort(state.sort)
    setColumnOrder(state.columnOrder)
    setHiddenColumnIdsArr(state.hiddenColumnIds)
    setPinnedColumnIdsArr(state.pinnedColumnIds)
    setDensity(state.density)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const savedViews = useSavedViews<BranchesSavedViewState>('branches', savedViewState, applySavedViewState)

  const exportColumns: ExportColumn<BranchView>[] = useMemo(
    () =>
      columnOrder
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined && !hiddenColumnIds.has(c.id))
        .map((c) => ({ id: c.id, header: c.header, toText: c.toExportText })),
    [columnOrder, hiddenColumnIds, columns],
  )

  function handleExport(format: ExportFormat) {
    runExport(format, 'Branches', branches, exportColumns)
  }
  function handleExportSelected(selectedIds: string[]) {
    const idSet = new Set(selectedIds)
    runExport('csv', 'Branches (selected)', branches.filter((b) => idSet.has(b.id)), exportColumns)
  }

  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : branches.length === 0
        ? hasAnyFilterOrSearch
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <>
      <ListView<BranchView>
        entityIcon="map-pin"
        title="Branches"
        countLabel={loading ? undefined : `${branches.length}${cursor ? '+' : ''}`}
        primaryAction={{ label: 'New branch', icon: 'plus', onClick: () => setModal({ mode: 'create' }) }}
        quickFilters={quickFilters}
        activeQuickFilterIds={activeQuickFilterIds}
        onQuickFilterToggle={onQuickFilterToggle}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search branches"
        searchPlaceholder="Search by name, country, region…"
        filterChildren={<LegalEntityFacet facets={facets} setFacets={setFacets} />}
        activeFilterCount={activeFilterChips.length}
        activeFilterChips={activeFilterChips}
        onRemoveFilterChip={removeFilterChip}
        onClearFilters={clearAllFilters}
        savedViews={savedViews}
        onExport={handleExport}
        exportNote={exportCapNote(branches.length, Boolean(cursor))}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumnIds={hiddenColumnIds}
        pinnedColumnIds={pinnedColumnIds}
        onToggleColumnHidden={toggleColumnHidden}
        onMoveColumn={moveColumn}
        onToggleColumnPin={toggleColumnPin}
        columnWidths={columnWidths}
        onColumnWidthChange={handleColumnWidthChange}
        sort={sort}
        onSortChange={setSort}
        density={density}
        onDensityChange={setDensity}
        state={state}
        rows={branches}
        getRowId={(row) => row.id}
        onRowActivate={(row) => navigate(`/clients/branches/${row.id}`)}
        renderRowActions={(row) => (
          <RowActionsMenu
            label={`Actions for ${row.name}`}
            actions={[
              { id: 'open', label: 'Open', icon: 'chevron-right', onSelect: () => navigate(`/clients/branches/${row.id}`) },
              { id: 'edit', label: 'Edit', icon: 'edit-3', onSelect: () => setModal({ mode: 'edit', branch: row }) },
            ]}
          />
        )}
        selection={selection}
        onSelectionChange={setSelection}
        bulkActions={[{ id: 'export-selected', label: 'Export selected', icon: 'download', onRun: handleExportSelected }]}
        errorMessage={error ?? undefined}
        onRetry={retry}
        emptyTenant={{
          icon: 'map-pin',
          title: 'No branches yet',
          description: 'Create the first branch for a legal entity to get started.',
          action: (
            <button type="button" className="iw-btn iw-btn--primary" onClick={() => setModal({ mode: 'create' })}>
              New branch
            </button>
          ),
        }}
        hasMore={Boolean(cursor)}
        onLoadMore={() => void fetchPage(cursor, false)}
        loadingMore={loadingMore}
      />

      {modal?.mode === 'create' ? (
        <BranchFormModal
          mode="create"
          onClose={() => setModal(null)}
          onSaved={(branch) => {
            setModal(null)
            setBranches((prev) => [branch, ...prev])
          }}
        />
      ) : null}
      {modal?.mode === 'edit' ? (
        <BranchFormModal
          mode="edit"
          editing={{ branch: modal.branch, version: modal.branch.version }}
          onClose={() => setModal(null)}
          onSaved={(branch) => {
            setModal(null)
            setBranches((prev) => prev.map((b) => (b.id === branch.id ? branch : b)))
          }}
        />
      ) : null}
    </>
  )
}

interface LegalEntityFacetProps {
  facets: FacetState
  setFacets: (updater: (prev: FacetState) => FacetState) => void
}

/** The Legal entity facet inside the FilterPanel — filters by `legalEntityId` (CLAUDE.md §4 cross-list rule). */
function LegalEntityFacet({ facets, setFacets }: LegalEntityFacetProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <LegalEntityLookupField
        id="branches-facet-legal-entity"
        label="Legal entity"
        value={facets.legalEntity}
        onChange={(next) => setFacets((prev) => ({ ...prev, legalEntity: next }))}
      />
    </div>
  )
}
